import React, {useEffect} from 'react'
import XLSX from 'xlsx'
import {makeStyles} from '@material-ui/core/styles'
import {Box, Button, IconButton, Typography} from '@material-ui/core'
import ClearIcon from '@material-ui/icons/Clear'
import CheckIcon from '@material-ui/icons/Check'
import {connect} from 'react-redux'
import {dataActions} from '../../../../../../actions'
import {ReactComponent as DownloadIcon} from '../../../../../../images/download-icon.svg'
import CircularProgress from '@material-ui/core/CircularProgress'

// using hook API
const useStyles = makeStyles(theme => ({
    boundaryBox: {
        display: 'flex',
        flexDirection: 'column',
        margin: '-15px 0 15px 0',
        fontSize: '0.825rem',
        alignItems: 'flex-start',
    },
    statusBox: {
        width: '100%',
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'space-between',
    },
    submitButton: {
        color: theme.palette.common.white,
        backgroundColor: theme.palette.primary.main,
        margin: theme.spacing(1, 0),
        boxShadow: 'none',
        borderRadius: theme.shape.borderRadius,
        height: '2.625rem',
        textTransform: 'uppercase',
        width: '48%',
        '&:hover': {
            boxShadow: 'none',
            backgroundColor: '#00652A',
        },
    },
    goBackButton: {
        margin: theme.spacing(1, 0),
        boxShadow: 'none',
        borderRadius: theme.shape.borderRadius,
        border: '1px solid #098447',
        color: theme.palette.primary.main,
        height: '2.625rem',
        textTransform: 'uppercase',
        width: '48%',
    },
    errorIcon: {
        color: theme.palette.error.borderColor,
    },
    spinnerBox: {
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'center',
        padding: '0 0 40px 0',
    },
    missingIDHeader: {
        color: theme.palette.error.borderColor,
        fontWeight: 700,
    },
}))

const BatchUploadViewResults = props => {
    const classes = useStyles()

    const {
        token,
        userName,
        clearbatchswipevalidate,
        fetchingSwipedEmplids,
        fetchingSwipeValidation,
        batchswipeValidatedResult,
        batchswipevalidate,
        eventoccurrence,
        batchSwipeArray,
        removeDuplicates,
        setFinalInvalidSwipeIds,
        missingHeaderFiles,
        callbackfunction,
        emplidsswiped,
        getbatchswipeemplids,
    } = props

    const {
        validCount,
        validSwipeIds,
        invalidSwipeIds,
    } = batchswipeValidatedResult

    const {
        deptId,
        eventId,
        occurId,
        duplicateAllow,
        occurTitle,
    } = eventoccurrence

    // remove duplicates within file
    const swipeIdArray = removeDuplicates
        ? [...new Set(batchSwipeArray)]
        : [...batchSwipeArray]

    // get swiped ids and call validate endpoint
    useEffect(() => {
        getbatchswipeemplids(token, deptId, eventId, occurId)
        batchswipevalidate(token, deptId, eventId, occurId, swipeIdArray)
        return () => {
            clearbatchswipevalidate()
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    // count number of instances for each emplid in valid id list returned by validate endpoint
    const checkDuplicateInValidIds = validSwipeIds
        ? validSwipeIds.reduce((acc, element) => {
              acc[element.emplid] = ++acc[element.emplid] || 0
              return acc
          }, {})
        : []

    let validIdsDuplicate = []
    let validIdsUniques = []

    // this step identifies and removes duplicate valid ids within the file
    if (checkDuplicateInValidIds) {
        for (const emplid in checkDuplicateInValidIds) {
            // keep first instance of valid emplids in array for unique
            validIdsUniques.push(emplid)
            if (checkDuplicateInValidIds[emplid] > 0) {
                for (
                    let dupCount = 0;
                    dupCount < checkDuplicateInValidIds[emplid];
                    dupCount++
                ) {
                    // push additional instances of valid emplids into array for duplicates
                    validIdsDuplicate.push(emplid)
                }
            }
        }
    }

    // get emplids not found in database
    const notFoundInDatabase = validIdsUniques.filter(
        val => !emplidsswiped.includes(val)
    )
    // get emplids found in database
    const foundInDatabase = validIdsUniques.filter(val =>
        emplidsswiped.includes(val)
    )

    // if occurrence Deny dupes, get count for duplicates in file and in database
    let duplicateSwipeIds =
        duplicateAllow === 'D'
            ? validIdsDuplicate.concat(foundInDatabase).map(emplid => ({
                  swipeId: emplid,
                  reason: 'already scanned',
              }))
            : []
    // combine duplicate ids with list of invalid ids
    const finalInvalidSwipeIds = invalidSwipeIds
        ? [...invalidSwipeIds, ...duplicateSwipeIds]
        : duplicateSwipeIds

    // determine final valid Id count
    const finalValidSwipeCount =
        duplicateAllow === 'D' ? notFoundInDatabase.length : validCount
    // determine final valid Id array
    const finalValidSwipeIds =
        duplicateAllow === 'D'
            ? notFoundInDatabase
            : validSwipeIds
            ? validSwipeIds.map(item => item.emplid)
            : []

    // get total count of all Ids in batch upload
    const totalCount =
        finalValidSwipeCount + Object.keys(finalInvalidSwipeIds).length
    const acceptedSwipes = finalValidSwipeIds.length > 0 ? true : false

    // get count of invalid ids grouped by reason for rejection
    const invalidGroupbyReason = finalInvalidSwipeIds
        ? finalInvalidSwipeIds.reduce(function(obj, v) {
              // increment or set the property
              // `(obj[v.status] || 0)` returns the property value if defined
              // or 0 ( since `undefined` is a falsy value
              if (v.reason.includes('name not found for emplId')) {
                  obj['name not found for emplId'] =
                      (obj['name not found for emplId'] || 0) + 1
              }

              if (v.reason.includes('nothing found for euid')) {
                  obj['nothing found for euid'] =
                      (obj['nothing found for euid'] || 0) + 1
              }
              if (v.reason.includes('currently employed here')) {
                  obj['currently employed'] =
                      (obj['currently employed'] || 0) + 1
              }
              if (v.reason.includes('currently enrolled')) {
                  obj['currently enrolled'] =
                      (obj['currently enrolled'] || 0) + 1
              }
              if (v.reason.includes('already scanned')) {
                  obj['already scanned'] = (obj['already scanned'] || 0) + 1
              }

              // return the updated object
              return obj
              // set the initial value as an object
          }, {})
        : {}

    const disableContinueButton =
        missingHeaderFiles.length > 0 || finalValidSwipeIds.length === 0
            ? true
            : false

    const missingHeaderFileItems = missingHeaderFiles.map(file => (
        <li key={'missingIDheader-' + file.path}>
            <Box className={classes.fileList}>{file.path}</Box>
        </li>
    ))

    const handleContinue = () => {
        setFinalInvalidSwipeIds([...finalValidSwipeIds])
        callbackfunction('Batch Upload Record Swipes')
    }

    // generate report file for download
    const handleDownloadExcel = () => {
        const wb = XLSX.utils.book_new()
        wb.Props = {
            Title: 'Report-' + occurTitle,
            Subject: occurTitle,
            Author: userName,
            CreatedDate: new Date(),
        }

        wb.SheetNames.push('Invalid Swipe Ids')
        const ws_data_invalidSwipeIDs =
            finalInvalidSwipeIds.length > 0
                ? finalInvalidSwipeIds
                : [{'Swipe Id': 'None'}]
        const ws_invalidSwipeIds = XLSX.utils.json_to_sheet(
            ws_data_invalidSwipeIDs
        )
        wb.Sheets['Invalid Swipe Ids'] = ws_invalidSwipeIds

        wb.SheetNames.push('Valid Swipe Ids')
        const ws_data_validSwipeIDs =
            finalValidSwipeIds.length > 0
                ? finalValidSwipeIds.map(id => {
                      return {'Swipe Id': id}
                  })
                : [{'Valid Swipe Id': 'None'}]
        const ws_validSwipeIds = XLSX.utils.json_to_sheet(ws_data_validSwipeIDs)
        wb.Sheets['Valid Swipe Ids'] = ws_validSwipeIds

        XLSX.writeFile(wb, 'Report-' + occurTitle + '.xlsx')
    }

    return (
        <>
            {fetchingSwipeValidation || fetchingSwipedEmplids ? (
                <Box className={classes.spinnerBox}>
                    <CircularProgress
                        style={{
                            color: '#00853e',
                        }}
                        thickness={3}
                    />
                </Box>
            ) : (
                <Box className={classes.boundaryBox}>
                    <Typography
                        variant="subtitle1"
                        style={{color: '#098447', margin: '5px 0'}}
                    >
                        Step 3 of 4: Review Swipe Data
                    </Typography>
                    {missingHeaderFiles.length > 0 ? (
                        <aside>
                            <Typography
                                align="left"
                                variant="body2"
                                className={classes.missingIDHeader}
                            >
                                Formatting Error. The ID header is missing from
                                the following files:
                            </Typography>
                            <ul>{missingHeaderFileItems}</ul>
                        </aside>
                    ) : (
                        <>
                            <Box className={classes.statusBox}>
                                <Typography
                                    align="left"
                                    variant="caption"
                                    style={{
                                        color: '#707070',
                                        marginBottom: '10px',
                                    }}
                                >
                                    <b>SWIPE STATUS</b>
                                </Typography>
                                <Typography
                                    align="left"
                                    variant="caption"
                                    style={{
                                        color: '#707070',
                                        marginBottom: '10px',
                                    }}
                                >
                                    <b>COUNT</b>
                                </Typography>
                            </Box>
                            {acceptedSwipes ? (
                                <Box className={classes.statusBox}>
                                    <div style={{display: 'flex'}}>
                                        <CheckIcon style={{color: '#098447'}} />
                                        <Typography
                                            variant="body2"
                                            style={{
                                                color: '#707070',
                                                margin: '0 0 10px 10px ',
                                            }}
                                        >
                                            Accepted
                                        </Typography>
                                    </div>
                                    <Typography
                                        variant="body2"
                                        style={{
                                            color: '#707070',
                                            fontWeight: '700',
                                            marginBottom: '10px',
                                        }}
                                    >
                                        {finalValidSwipeCount}
                                    </Typography>
                                </Box>
                            ) : null}
                            {invalidGroupbyReason['currently enrolled'] > 0 ? (
                                <Box className={classes.statusBox}>
                                    <div style={{display: 'flex'}}>
                                        <ClearIcon
                                            className={classes.errorIcon}
                                        />
                                        <Typography
                                            variant="body2"
                                            style={{
                                                color: '#707070',
                                                margin: '0 0 10px 10px ',
                                            }}
                                        >
                                            Denied - Not Enrolled
                                        </Typography>
                                    </div>
                                    <Typography
                                        variant="body2"
                                        style={{
                                            color: '#707070',
                                            fontWeight: '700',
                                            marginBottom: '10px',
                                        }}
                                    >
                                        {
                                            invalidGroupbyReason[
                                                'currently enrolled'
                                            ]
                                        }
                                    </Typography>
                                </Box>
                            ) : null}
                            {invalidGroupbyReason['currently employed'] > 0 ? (
                                <Box className={classes.statusBox}>
                                    <div style={{display: 'flex'}}>
                                        <ClearIcon
                                            className={classes.errorIcon}
                                        />
                                        <Typography
                                            variant="body2"
                                            style={{
                                                color: '#707070',
                                                //fontWeight: '700',
                                                margin: '0 0 10px 10px ',
                                            }}
                                        >
                                            Denied - Not an Employee
                                        </Typography>
                                    </div>
                                    <Typography
                                        variant="body2"
                                        style={{
                                            color: '#707070',
                                            fontWeight: '700',
                                            marginBottom: '10px',
                                        }}
                                    >
                                        {
                                            invalidGroupbyReason[
                                                'currently employed'
                                            ]
                                        }
                                    </Typography>
                                </Box>
                            ) : null}
                            {invalidGroupbyReason['already scanned'] > 0 ? (
                                <Box className={classes.statusBox}>
                                    <div style={{display: 'flex'}}>
                                        <ClearIcon
                                            className={classes.errorIcon}
                                        />
                                        <Typography
                                            variant="body2"
                                            style={{
                                                color: '#707070',
                                                //fontWeight: '700',
                                                margin: '0 0 10px 10px ',
                                            }}
                                        >
                                            Denied - Swipe Already Recorded
                                        </Typography>
                                    </div>
                                    <Typography
                                        variant="body2"
                                        style={{
                                            color: '#707070',
                                            fontWeight: '700',
                                            marginBottom: '10px',
                                        }}
                                    >
                                        {
                                            invalidGroupbyReason[
                                                'already scanned'
                                            ]
                                        }
                                    </Typography>
                                </Box>
                            ) : null}
                            {invalidGroupbyReason['nothing found for euid'] >
                            0 ? (
                                <Box className={classes.statusBox}>
                                    <div style={{display: 'flex'}}>
                                        <ClearIcon
                                            className={classes.errorIcon}
                                        />
                                        <Typography
                                            variant="body2"
                                            style={{
                                                color: '#707070',
                                                margin: '0 0 10px 10px ',
                                            }}
                                        >
                                            Denied - Incorrect Format
                                        </Typography>
                                    </div>
                                    <Typography
                                        variant="body2"
                                        style={{
                                            color: '#707070',
                                            fontWeight: '700',
                                            marginBottom: '10px',
                                        }}
                                    >
                                        {
                                            invalidGroupbyReason[
                                                'nothing found for euid'
                                            ]
                                        }
                                    </Typography>
                                </Box>
                            ) : null}
                            {invalidGroupbyReason['name not found for emplId'] >
                            0 ? (
                                <Box className={classes.statusBox}>
                                    <div style={{display: 'flex'}}>
                                        <ClearIcon
                                            className={classes.errorIcon}
                                        />
                                        <Typography
                                            variant="body2"
                                            style={{
                                                color: '#707070',
                                                margin: '0 0 10px 10px ',
                                            }}
                                        >
                                            Denied - Name not found
                                        </Typography>
                                    </div>
                                    <Typography
                                        variant="body2"
                                        style={{
                                            color: '#707070',
                                            fontWeight: '700',
                                            marginBottom: '10px',
                                        }}
                                    >
                                        {
                                            invalidGroupbyReason[
                                                'name not found for emplId'
                                            ]
                                        }
                                    </Typography>
                                </Box>
                            ) : null}
                            <Box className={classes.statusBox}>
                                <Typography
                                    align="left"
                                    variant="body2"
                                    style={{
                                        color: '#262626',
                                        marginBottom: '10px',
                                    }}
                                >
                                    Total Records Found
                                </Typography>
                                <Typography
                                    variant="body2"
                                    style={{
                                        color: '#707070',
                                        fontWeight: '700',
                                        marginBottom: '10px',
                                    }}
                                >
                                    {totalCount ? totalCount : 0}
                                </Typography>
                            </Box>
                            <IconButton
                                style={{
                                    padding: '12px 12px 12px 0',
                                    marginLeft: '-5px',
                                }}
                                onClick={() => handleDownloadExcel()}
                            >
                                <DownloadIcon
                                    style={{
                                        color: '#098447',
                                        height: '35px',
                                        padding: '0',
                                    }}
                                />
                                <Typography
                                    variant="body2"
                                    style={{
                                        color: '#098447',
                                        fontWeight: '700',
                                    }}
                                >
                                    DOWNLOAD DETAILED REPORT
                                </Typography>
                            </IconButton>
                        </>
                    )}
                </Box>
            )}
            <Box className={classes.boundaryBox}>
                <Box className={classes.statusBox}>
                    <Button
                        variant="outlined"
                        size="small"
                        //disabled={!confirmAction}
                        className={classes.goBackButton}
                        onClick={() => callbackfunction('Batch Upload Home')}
                    >
                        Go Back
                    </Button>
                    <Button
                        variant="contained"
                        size="small"
                        disabled={disableContinueButton}
                        className={classes.submitButton}
                        onClick={() => handleContinue()}
                    >
                        Continue
                    </Button>
                </Box>
            </Box>
        </>
    )
}

function mapStateToProps(state) {
    return {
        token: state.authentication.token,
        fetchingSwipeValidation: state.batchswipedata.fetchingSwipeResult,
        fetchingSwipedEmplids: state.batchswipedata.fetchingSwipedEmplids,
        batchswipeValidatedResult: state.batchswipedata.batchswiperesult,
        emplidsswiped: state.batchswipedata.emplidsswiped,
        userName: state.authentication.displayname,
    }
}

const mapDispatchToProps = {
    batchswipevalidate: (token, deptId, eventId, occurId, swipeIdArray) =>
        dataActions.batchswipevalidateRequest(
            token,
            deptId,
            eventId,
            occurId,
            swipeIdArray
        ),
    getbatchswipeemplids: (token, deptId, eventId, occurId) =>
        dataActions.getbatchswipeemplidsRequest(
            token,
            deptId,
            eventId,
            occurId
        ),
    clearbatchswipevalidate: () => dataActions.batchswipevalidateClear(),
}

export default connect(
    mapStateToProps,
    mapDispatchToProps
)(BatchUploadViewResults)
